// frontend/src/pages/orgaos/OrgaosListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function OrgaosListPage() {
  const { loading: authLoading, hasPermission } = useAuth();
  const canManage = hasPermission('config.orgaos');

  const [orgaos, setOrgaos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchOrgaos = async () => {
      setLoading(true);
      setError(null);

      if (!canManage) {
        setError({ kind: 'permission' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/orgaos', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        setOrgaos(response?.data?.data ?? []);
        setPaginationInfo(response?.data?.meta ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchOrgaos();
    return () => controller.abort();
  }, [authLoading, canManage, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : orgaos.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza de que deseja desativar este órgão?')) return;
    try {
      await api.delete(`/orgaos/${id}`);
      setOrgaos((prev) => prev.filter((o) => o.id !== id));
    } catch (err) {
      if (err?.response?.status === 403) {
        alert('Acesso não permitido.');
      } else {
        alert('Ocorreu um erro ao desativar o órgão.');
      }
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Tem certeza de que deseja reativar este órgão?')) return;
    try {
      await api.patch(`/orgaos/${id}/restore`);
      setOrgaos((prev) => prev.filter((o) => o.id !== id));
    } catch (err) {
      if (err?.response?.status === 403) {
        alert('Acesso não permitido.');
      } else {
        alert('Ocorreu um erro ao reativar o órgão.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className="orgaos-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orgaos-page">
        <LoadingSpinner text="Carregando órgãos..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="orgaos-page">
          <PermissionDeniedAlert
            requiredPermission="config.orgaos"
            action="listar órgãos"
          />
        </div>
      );
    }
    return (
      <div className="orgaos-page">
        <AlertException error={error} fallback="Não foi possível carregar os órgãos." />
      </div>
    );
  }

  return (
    <div className="orgaos-page">
      <div className="content-header">
        <h1 className="content-title">Órgãos</h1>
        <p className="content-subtitle">Lista de órgãos cadastrados no sistema</p>
      </div>

      <div className="card orgaos-list-card">
        <div className="card-body">
          <div className="list-toolbar orgaos-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="orgaos-filters-group d-flex align-items-center gap-3">
              <div className="input-group orgaos-search-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome ou sigla..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="orgaos-status-filter d-flex align-items-center gap-2">
                <label htmlFor="orgaosStatusFilter" className="mb-0 orgaos-status-label">Filtrar por:</label>
                <select
                  id="orgaosStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar órgãos por status"
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="orgaos-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            <Link to="/painel/orgaos/novo" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              Novo Órgão
            </Link>
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Sigla</th>
                  <th>Data de Criação</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {orgaos.length > 0 ? (
                  orgaos.map((orgao) => {
                    const isInactive = Boolean(orgao.deleted_at);

                    return (
                      <tr key={orgao.id}>
                        <td data-label="Nome">{orgao.nome}</td>
                        <td data-label="Sigla">{orgao.sigla}</td>
                        <td data-label="Data de Criação">{orgao.created_at ? String(orgao.created_at).split(' ')[0] : '-'}</td>
                        <td data-label="Status">{isInactive ? 'Inativo' : 'Ativo'}</td>
                        <td className="table-actions orgaos-table-actions d-flex gap-2" data-label="Ações">
                          {!isInactive && (
                            <Link
                              to={`/painel/orgaos/${orgao.id}/editar`}
                              className="btn btn-sm btn-outline-warning"
                              title="Editar"
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}
                          {!isInactive && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Desativar"
                              onClick={() => handleDelete(orgao.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                          {isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(orgao.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">Nenhum órgão encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="orgaos-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> -{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrgaosListPage;
